import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function useFavorites() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: favoriteIds = [], isLoading } = useQuery({
    queryKey: ["guest-favorites", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("guest_favorites")
        .select("experience_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data.map((f) => f.experience_id);
    },
  });

  const toggleFavorite = useMutation({
    mutationFn: async (experienceId: string) => {
      if (!user) throw new Error("Not authenticated");
      const isFav = favoriteIds.includes(experienceId);
      if (isFav) {
        await supabase
          .from("guest_favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("experience_id", experienceId);
      } else {
        await supabase
          .from("guest_favorites")
          .insert({ user_id: user.id, experience_id: experienceId });
      }
    },
    onMutate: async (experienceId) => {
      await queryClient.cancelQueries({ queryKey: ["guest-favorites", user?.id] });
      const previous = queryClient.getQueryData<string[]>(["guest-favorites", user?.id]) ?? [];
      const next = previous.includes(experienceId)
        ? previous.filter((id) => id !== experienceId)
        : [...previous, experienceId];
      queryClient.setQueryData(["guest-favorites", user?.id], next);
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["guest-favorites", user?.id], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-favorites", user?.id] });
    },
  });

  const isFavorite = (experienceId: string) => favoriteIds.includes(experienceId);

  return { favoriteIds, isFavorite, toggleFavorite, isLoading };
}
